import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../providers/ApiProvider';
import { useAuth } from '../providers/AuthProvider';

// Types to mirror backend ChatRequest payload
type MessageRole = 'user' | 'assistant' | 'system';

interface HistoryItem {
    role: MessageRole;
    content: string;
}

interface ChatResponse {
    content: string;
    response_id: string;
    model_used: string;
    functions_executed: number;
    previous_response_id?: string | null;
}

export const ChatPage: React.FC = () => {
    const { api } = useApi();
    const { user } = useAuth();

    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [input, setInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sessionId] = useState(() => `web-${Math.random().toString(36).slice(2)}`);
    const [error, setError] = useState<string | null>(null);

    const chatContainerRef = useRef<HTMLDivElement | null>(null);

    const canSend = input.trim().length > 0 && !isSending;

    const userDisplayName = useMemo(() => {
        if (!user) return undefined;
        return user.display_name || user.first_name || user.email || `user-${user.id}`;
    }, [user]);

    const userEmail = user?.email;
    const userId = user ? String(user.id) : undefined;

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [history, isSending]);

    const sendMessage = async () => {
        if (!canSend) return;

        const message = input.trim();
        setInput('');
        setError(null);

        // Optimistically add user message
        setHistory(prev => [...prev, { role: 'user', content: message }]);
        setIsSending(true);

        try {
            const payload = {
                message,
                session_id: sessionId,
                user_id: userId,
                name: userDisplayName,
                email: userEmail,
                role: 'user',
                system_prompt: 'You are a helpful company assistant with access to a knowledge base via rag_search.',
                model: 'gpt-5-mini',
                max_tokens: 7000,
                history: history.map(h => ({ role: h.role, content: h.content })),
            };

            const { data } = await api.post<ChatResponse>('/api/v1/chat/rag', payload);

            setHistory(prev => [...prev, { role: 'assistant', content: data.content }]);
        } catch (e: any) {
            const message = e?.response?.data?.error?.message || e?.message || 'Failed to send message';
            setError(message);
            // Add error bubble from assistant
            setHistory(prev => [
                ...prev,
                { role: 'assistant', content: 'Sorry, an error occurred while processing the request.' },
            ]);
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canSend) sendMessage();
        }
    };

    return (
        <div className="container" style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' }}>
            <div
                style={{
                    background: 'white',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 10px 30px rgba(17,24,39,0.06)',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '1rem 1.25rem',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                        }}
                    >
                        AI
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>AI Assistant Chat</div>
                        <div style={{ color: '#6b7280', fontSize: 13 }}>Demo - Friant - Pricebook</div>
                    </div>
                </div>

                {/* Chat area */}
                <div
                    ref={chatContainerRef}
                    style={{
                        height: '60vh',
                        overflowY: 'auto',
                        padding: '1rem 1.25rem',
                        background: '#f9fafb',
                    }}
                >
                    {history.length === 0 && (
                        <div
                            style={{
                                textAlign: 'center',
                                color: '#374151',
                                marginTop: 40,
                                padding: '2rem 1rem',
                            }}
                        >
                            {/* Welcome Header */}
                            <div
                                style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: '#1f2937',
                                    marginBottom: '0.5rem',
                                    background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                👋 Hello and Welcome!
                            </div>

                            <div
                                style={{
                                    fontSize: '1rem',
                                    color: '#6b7280',
                                    marginBottom: '2rem',
                                    lineHeight: '1.6',
                                }}
                            >
                                I'm your AI assistant for <strong>Friant and Associates</strong> price book
                            </div>

                            {/* Features List */}
                            <div
                                style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    margin: '0 auto',
                                    maxWidth: '500px',
                                    boxShadow: '0 4px 20px rgba(17,24,39,0.08)',
                                    border: '1px solid #e5e7eb',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '1.1rem',
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    Here's how I can help you:
                                </div>

                                <div style={{ textAlign: 'left' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '0.75rem',
                                            padding: '0.5rem',
                                            borderRadius: '8px',
                                            background: '#f8fafc',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: '#3b82f6',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                marginRight: '0.75rem',
                                                flexShrink: 0,
                                            }}
                                        >
                                            1
                                        </div>
                                        <span style={{ color: '#374151', fontSize: '0.95rem' }}>
                                            Tell you about <strong>Friant's products and services</strong>
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '0.75rem',
                                            padding: '0.5rem',
                                            borderRadius: '8px',
                                            background: '#f8fafc',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: '#06b6d4',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                marginRight: '0.75rem',
                                                flexShrink: 0,
                                            }}
                                        >
                                            2
                                        </div>
                                        <span style={{ color: '#374151', fontSize: '0.95rem' }}>
                                            Direct your contact information to the <strong>proper Friant Associate</strong>
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0.5rem',
                                            borderRadius: '8px',
                                            background: '#f8fafc',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: '#10b981',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                marginRight: '0.75rem',
                                                flexShrink: 0,
                                            }}
                                        >
                                            3
                                        </div>
                                        <span style={{ color: '#374151', fontSize: '0.95rem' }}>
                                            Answer questions about <strong>prices and models</strong>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Call to Action */}
                            <div
                                style={{
                                    marginTop: '1.5rem',
                                    fontSize: '0.9rem',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                }}
                            >
                                Feel free to ask me anything! 💬
                            </div>
                        </div>
                    )}

                    {history.map((msg, idx) => (
                        <div key={idx} style={{ marginBottom: 12, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                            <div
                                style={{
                                    maxWidth: '75%',
                                    padding: '10px 14px',
                                    borderRadius: 14,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    background: msg.role === 'user' ? '#3b82f6' : 'white',
                                    color: msg.role === 'user' ? 'white' : '#111827',
                                    border: msg.role === 'user' ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                    boxShadow: '0 2px 10px rgba(17,24,39,0.05)'
                                }}
                            >
                                {msg.content}
                            </div>
                        </div>
                    ))}

                    {/* Typing indicator */}
                    {isSending && (
                        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-start' }}>
                            <div
                                style={{
                                    maxWidth: '60%',
                                    padding: '10px 14px',
                                    borderRadius: 14,
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                    boxShadow: '0 2px 10px rgba(17,24,39,0.05)'
                                }}
                            >
                                typing...
                            </div>
                        </div>
                    )}
                </div>

                {/* Composer */}
                <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', background: 'white' }}>
                    {error && (
                        <div style={{ color: '#b91c1c', marginBottom: 8, fontSize: 14 }}>{error}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={isSending ? 'Wait for the assistant\'s response...' : 'Enter a message and press Enter'}
                            disabled={isSending}
                            rows={1}
                            style={{
                                flex: 1,
                                resize: 'none',
                                border: '1px solid #d1d5db',
                                borderRadius: 10,
                                padding: '10px 12px',
                                fontSize: 15,
                                lineHeight: 1.5,
                                outline: 'none',
                                background: isSending ? '#f3f4f6' : 'white'
                            }}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!canSend}
                            className="btn btn-primary"
                            style={{
                                minWidth: 96,
                                height: 42,
                                opacity: canSend ? 1 : 0.6,
                            }}
                        >
                            Send
                        </button>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                        Press Enter to send, Shift+Enter — new line
                    </div>
                </div>
            </div>
        </div>
    );
}; 